/**
 * PATH: packages/auth/src/HubGuard.tsx
 * PURPOSE: Route-level guard that blocks access to a hub when the user lacks
 *          the required RBAC permission (module + action).
 *
 * WHY: The backend HubAccessMiddleware returns 403 on API calls, but without
 *      a frontend guard the user sees an empty/broken page. This component
 *      renders a clean "Access Denied" screen instead.
 *
 * FLOW:
 *   useRBAC() → check accessibleModules / usePermission → render children or fallback
 *
 * DEPENDENCIES:
 *   - ./rbac-state: useRBAC
 *   - ./usePermission: usePermission
 */

import { type ReactNode } from 'react'
import { useRBAC } from './rbac-state'

export interface HubGuardProps {
  hub: string
  children: ReactNode
  fallback?: ReactNode
}

function DefaultAccessDenied({ hub }: { hub: string }) {
  const hubLabel = hub.charAt(0).toUpperCase() + hub.slice(1)
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="text-center max-w-md mx-auto px-6">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
          Access Denied
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          You don't have permission to access the {hubLabel} Hub.
          Contact your organisation administrator to request access.
        </p>
        <a
          href="https://dashboard.quotech.ai"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  )
}

/**
 * Guard component that blocks hub rendering if the user lacks access.
 *
 * Checks `accessibleModules` from the RBAC state. If the RBAC system
 * hasn't loaded yet or returned an error, children render normally so
 * the backend middleware handles enforcement (graceful degradation).
 *
 * @example
 * <HubGuard hub="cfo">
 *   <CFOLayout />
 * </HubGuard>
 */
export function HubGuard({ hub, children, fallback }: HubGuardProps) {
  const { accessibleModules, loading, error, isGlobalAdmin, isOrgAdmin } = useRBAC()

  if (isGlobalAdmin) return <>{children}</>

  if (loading) return <>{children}</>

  // RBAC fetch failed — only org admins (from fallback detection) pass through
  if (error) {
    if (isOrgAdmin) return <>{children}</>
    return <>{fallback || <DefaultAccessDenied hub={hub} />}</>
  }

  // RBAC succeeded but user has no modules assigned
  if (accessibleModules.length === 0) {
    return <>{fallback || <DefaultAccessDenied hub={hub} />}</>
  }

  if (!accessibleModules.includes(hub)) {
    return <>{fallback || <DefaultAccessDenied hub={hub} />}</>
  }

  return <>{children}</>
}

export default HubGuard
